/**
 * F-170 B3.5 — Maplibre GL service-area surface.
 *
 * Two modes:
 *   - `view`  : read-only — renders the polygon (or radius circle) plus
 *               the center marker. Used on `SpecialistProfilePage`.
 *   - `edit`  : adds Mapbox Draw polygon tool + drag-marker so the
 *               specialist can author the GeoJSON in their cabinet.
 *
 * OSM raster tiles via `tile.openstreetmap.org`. Default center is
 * Lisboa (38.7223, -9.1393) because our seed geo and MVP audience are
 * Portugal-bound.
 *
 * The Mapbox Draw plugin is compatible with Maplibre per their own
 * docs; we strip the `mapboxgl` global hack by aliasing on the window.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef } from 'react';
import maplibregl, { LngLatLike, Map as MaplibreMap, Marker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { ServiceArea, ServiceAreaKind } from '@/types/domain';
import { logger } from '@/utils/logger';
import styles from './ServiceAreaMap.module.css';

// Mapbox Draw expects a `mapboxgl` global. Maplibre is API-compatible —
// we alias once at module load so the plugin can talk to our map.
declare global {
  interface Window {
    mapboxgl?: typeof maplibregl;
  }
}
if (typeof window !== 'undefined' && !window.mapboxgl) {
  window.mapboxgl = maplibregl;
}

const DEFAULT_CENTER: [number, number] = [-9.1393, 38.7223]; // Lisboa [lng, lat]
const DEFAULT_ZOOM = 11;

const OSM_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: [
        'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
        'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
        'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
    },
  },
  layers: [{ id: 'osm-tiles', type: 'raster', source: 'osm' }],
};

const KIND_LABEL: Record<ServiceAreaKind, string> = {
  reception: 'Прийом у себе',
  delivery_zone: 'Зона доставки',
  service_area: 'Зона виїзду',
};

export interface ServiceAreaMapProps {
  mode: 'view' | 'edit';
  value: ServiceArea | null;
  /** Required when `mode === 'edit'`. Emits the next ServiceArea on
   *  every polygon edit / marker drag. `kind` is preserved from the
   *  incoming value (or defaults to `service_area` for greenfield). */
  onChange?: (next: ServiceArea) => void;
}

function toNumber(raw: string | number | null | undefined, fallback: number): number {
  if (raw === null || raw === undefined || raw === '') return fallback;
  const n = typeof raw === 'number' ? raw : parseFloat(raw);
  return Number.isFinite(n) ? n : fallback;
}

export function ServiceAreaMap({
  mode,
  value,
  onChange,
}: ServiceAreaMapProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MaplibreMap | null>(null);
  const markerRef = useRef<Marker | null>(null);
  const drawRef = useRef<any | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Initialise the map once. Re-initialisations happen via dedicated
  // effects below (marker / polygon / draw state).
  useEffect(() => {
    if (!containerRef.current) return;
    const center: LngLatLike = value
      ? [toNumber(value.center_lng, DEFAULT_CENTER[0]), toNumber(value.center_lat, DEFAULT_CENTER[1])]
      : DEFAULT_CENTER;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: OSM_STYLE,
      center,
      zoom: DEFAULT_ZOOM,
      attributionControl: { compact: true },
    });
    mapRef.current = map;

    map.on('load', () => {
      // Polygon layer for `view` mode. In `edit` mode the Draw plugin
      // renders its own overlay so we skip this.
      if (mode === 'view' && value?.polygon_geojson) {
        map.addSource('area', {
          type: 'geojson',
          data: { type: 'Feature', geometry: value.polygon_geojson, properties: {} },
        });
        map.addLayer({
          id: 'area-fill',
          type: 'fill',
          source: 'area',
          paint: { 'fill-color': '#2481cc', 'fill-opacity': 0.18 },
        });
        map.addLayer({
          id: 'area-outline',
          type: 'line',
          source: 'area',
          paint: { 'line-color': '#2481cc', 'line-width': 2 },
        });
      }

      // Center marker (both modes).
      const marker = new maplibregl.Marker({ color: '#2481cc', draggable: mode === 'edit' })
        .setLngLat(center)
        .addTo(map);
      markerRef.current = marker;

      if (mode === 'edit') {
        marker.on('dragend', () => {
          const ll = marker.getLngLat();
          emitChange({ center_lat: ll.lat, center_lng: ll.lng });
        });

        // Mapbox Draw: dynamic import to keep the bundle lean for
        // view-mode pages that never edit.
        import('@mapbox/mapbox-gl-draw')
          .then((mod) => {
            const MapboxDraw = (mod as any).default ?? mod;
            const draw = new MapboxDraw({
              displayControlsDefault: false,
              controls: { polygon: true, trash: true },
              defaultMode: 'simple_select',
            });
            map.addControl(draw as any);
            drawRef.current = draw;
            if (value?.polygon_geojson) {
              draw.add({
                type: 'Feature',
                geometry: value.polygon_geojson,
                properties: {},
              });
            }
            const handle = (): void => {
              const fc = draw.getAll();
              const poly = fc.features.find((f: any) => f.geometry.type === 'Polygon');
              emitChange({ polygon: poly ? (poly.geometry as GeoJSON.Polygon) : null });
            };
            map.on('draw.create', handle);
            map.on('draw.update', handle);
            map.on('draw.delete', handle);
          })
          .catch((err) => logger.warn('mapbox-gl-draw load failed', err));
      }
    });

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
      drawRef.current = null;
    };
    // mode / initial value drive the first paint; downstream updates go
    // through their own effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Sync marker + polygon when the controlled `value` changes
  // externally (e.g. after a successful PUT).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    if (value) {
      const ll: LngLatLike = [
        toNumber(value.center_lng, DEFAULT_CENTER[0]),
        toNumber(value.center_lat, DEFAULT_CENTER[1]),
      ];
      markerRef.current?.setLngLat(ll);
    }
  }, [value]);

  function emitChange(patch: {
    center_lat?: number;
    center_lng?: number;
    polygon?: GeoJSON.Polygon | null;
  }): void {
    const cb = onChangeRef.current;
    if (!cb) return;
    const base: ServiceArea = value ?? {
      id: 0,
      kind: 'service_area',
      center_lat: DEFAULT_CENTER[1],
      center_lng: DEFAULT_CENTER[0],
      polygon_geojson: null,
      radius_km: null,
      address_text: '',
    };
    const next: ServiceArea = {
      ...base,
      center_lat: patch.center_lat ?? toNumber(base.center_lat, DEFAULT_CENTER[1]),
      center_lng: patch.center_lng ?? toNumber(base.center_lng, DEFAULT_CENTER[0]),
      polygon_geojson:
        patch.polygon !== undefined ? patch.polygon : base.polygon_geojson,
    };
    cb(next);
  }

  const kindLabel = value ? KIND_LABEL[value.kind] : null;

  return (
    <div>
      {kindLabel ? <span className={styles.kindLabel}>{kindLabel}</span> : null}
      <div
        ref={containerRef}
        className={[styles.map, mode === 'edit' ? styles.mapEdit : '']
          .filter(Boolean)
          .join(' ')}
        aria-label="Карта зони обслуговування"
      />
      {value?.address_text ? (
        <p className={styles.addressNote}>{value.address_text}</p>
      ) : null}
    </div>
  );
}
